import CodeMirror, { EditorView, ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { indentWithTab } from "@codemirror/commands";
import { indentUnit } from "@codemirror/language";
import { keymap, lineNumbers, placeholder } from "@codemirror/view";
import { search } from "@codemirror/search";
import { EditorState, Extension, Prec } from "@codemirror/state";
import type { CompletionContext, CompletionResult } from "@codemirror/autocomplete";
import { githubDark, githubLight } from "@uiw/codemirror-theme-github";
import { forwardRef, useMemo, useRef } from "react";
import { continueListOnEnter } from "../utils/editorCommands";

interface MarkdownEditorProps {
  value: string;
  darkMode: boolean;
  fontSize: number;
  tabSize: number;
  showLineNumbers: boolean;
  wordWrap: boolean;
  spellcheck: boolean;
  isFullscreen: boolean;
  /** Vault note names, offered as `[[` autocompletions. */
  noteNames: string[];
  onChange: (value: string) => void;
  onScroll: (ratio: number) => void;
  onDropFiles: (files: FileList) => void;
}

export const MarkdownEditor = forwardRef<ReactCodeMirrorRef, MarkdownEditorProps>(
  ({ value, darkMode, fontSize, tabSize, showLineNumbers, wordWrap, spellcheck, isFullscreen, noteNames, onChange, onScroll, onDropFiles }, ref) => {
    // Read live names from a ref so the editor config isn't rebuilt on every rename.
    const namesRef = useRef(noteNames);
    namesRef.current = noteNames;

    const extensions = useMemo(() => {
      // `[[` autocomplete: suggest existing notes, insert `Name]]` and skip past
      // an auto-closed `]]` if close-brackets already added one.
      const wikiComplete = (context: CompletionContext): CompletionResult | null => {
        const token = context.matchBefore(/\[\[([^[\]\n]*)$/);
        if (!token) return null;
        if (token.from === token.to && !context.explicit) return null;
        const typed = token.text.slice(2).toLowerCase();
        const from = token.from + 2;
        const options = namesRef.current
          .filter((name) => name.toLowerCase().includes(typed))
          .slice(0, 50)
          .map((name) => ({
            label: name,
            type: "text",
            apply: (view: EditorView, _completion: unknown, applyFrom: number, applyTo: number) => {
              const trailing = view.state.sliceDoc(applyTo, applyTo + 2) === "]]";
              const insert = trailing ? name : `${name}]]`;
              view.dispatch({
                changes: { from: applyFrom, to: applyTo, insert },
                selection: { anchor: applyFrom + insert.length + (trailing ? 2 : 0) },
              });
            },
          }));
        if (!options.length) return null;
        return { from, options, validFor: /^[^[\]\n]*$/ };
      };

      const list: Extension[] = [
        markdown(),
        markdownLanguage.data.of({ autocomplete: wikiComplete }),
        search({ top: true }),
        // High precedence so smart list/quote continuation wins over the
        // default Enter (plain newline); it returns false to fall through
        // when the cursor isn't on a list/quote line.
        Prec.high(keymap.of([{ key: "Enter", run: continueListOnEnter }])),
        keymap.of([indentWithTab]),
        indentUnit.of(" ".repeat(tabSize)),
        EditorState.tabSize.of(tabSize),
        placeholder("Start writing… Markdown, images, and Mermaid diagrams are all welcome."),
      ];
      if (showLineNumbers) list.push(lineNumbers());
      if (wordWrap) list.push(EditorView.lineWrapping);
      if (spellcheck) list.push(EditorView.contentAttributes.of({ spellcheck: "true", autocorrect: "on" }));

      return list;
    }, [tabSize, showLineNumbers, wordWrap, spellcheck]);

    return (
      <div
        className="editor-shell"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (event.dataTransfer.files.length) onDropFiles(event.dataTransfer.files);
        }}
        onPasteCapture={(event) => {
          // Hijack the paste only for pure image payloads (e.g. screenshots);
          // mixed clipboards that carry text keep CodeMirror's default paste.
          const data = event.clipboardData;
          if (!data?.files.length) return;
          const hasImage = Array.from(data.files).some((file) => file.type.startsWith("image/"));
          if (!hasImage || data.getData("text/plain")) return;
          event.preventDefault();
          event.stopPropagation();
          onDropFiles(data.files);
        }}
      >
        <CodeMirror
          ref={ref}
          value={value}
          height={isFullscreen ? "calc(100vh - 132px)" : "100%"}
          theme={darkMode ? githubDark : githubLight}
          extensions={extensions}
          basicSetup={{
            foldGutter: true,
            highlightActiveLine: true,
            autocompletion: true,
            bracketMatching: true,
            closeBrackets: true,
            history: true,
          }}
          onChange={onChange}
          onUpdate={(update) => {
            const scroller = update.view.scrollDOM;
            const max = scroller.scrollHeight - scroller.clientHeight;
            onScroll(max <= 0 ? 0 : scroller.scrollTop / max);
          }}
          style={{ fontSize }}
          aria-label="Markdown editor"
        />
      </div>
    );
  },
);

MarkdownEditor.displayName = "MarkdownEditor";
